import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Popover } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import { ReactComponent as CalendarIcon } from '../../../assets/PepWise2.0/Calendar.svg';
import { ReactComponent as CalendarIconActive } from '../../../assets/PepWise2.0/Calendar-Active.svg';
import { ReactComponent as RightArrowBoxIcon } from '../../../assets/PepWise2.0/Right-Arrow-Icon-Box.svg';
import { ReactComponent as LeftArrowBoxIcon } from '../../../assets/PepWise2.0/Left-Arrow-Icon-Box.svg';

const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

type Props = {
  value: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  minDate?: Dayjs | null;
  maxDate?: Dayjs | null;
};

const getMonthMatrix = (month: Dayjs) => {
  const start = month.startOf('month').startOf('week');
  const end = month.endOf('month').endOf('week');

  let current = start.clone();
  const matrix: Dayjs[][] = [];

  while (current.isBefore(end) || current.isSame(end, 'day')) {
    const week: Dayjs[] = [];

    for (let i = 0; i < 7; i++) {
      week.push(current.clone());
      current = current.add(1, 'day');
    }

    matrix.push(week);
  }

  return matrix;
};

export default function BasicDateFilter({
  value,
  onChange,
  minDate = null,
  maxDate = null,
}: Props) {
  const theme = useTheme();
  const colors = theme.palette;

  const today = dayjs().endOf('day');

  const effectiveMaxDate = useMemo(() => {
    if (!maxDate) return today;
    return maxDate.isBefore(today, 'day') ? maxDate.endOf('day') : today;
  }, [maxDate, today]);

  const effectiveMinDate = useMemo(() => {
    return minDate ? minDate.startOf('day') : null;
  }, [minDate]);

  const initialMonth = useMemo(() => {
    if (value) return value;
    if (effectiveMaxDate) return effectiveMaxDate;
    return dayjs();
  }, [value, effectiveMaxDate]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [month, setMonth] = useState(initialMonth);

  const open = Boolean(anchorEl);
  const matrix = getMonthMatrix(month);

  const isDisabledDate = (date: Dayjs) => {
    if (effectiveMinDate && date.isBefore(effectiveMinDate, 'day')) return true;
    if (effectiveMaxDate && date.isAfter(effectiveMaxDate, 'day')) return true;
    return false;
  };

  const canGoPrevMonth = useMemo(() => {
    if (!effectiveMinDate) return true;
    const prevMonth = month.subtract(1, 'month');
    return !prevMonth.endOf('month').isBefore(effectiveMinDate, 'day');
  }, [month, effectiveMinDate]);

  const canGoNextMonth = useMemo(() => {
    const nextMonth = month.add(1, 'month');
    return !nextMonth.startOf('month').isAfter(effectiveMaxDate, 'day');
  }, [month, effectiveMaxDate]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setMonth(value || effectiveMaxDate || dayjs());
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateClick = (date: Dayjs) => {
    if (isDisabledDate(date)) return;
    onChange(date);
    handleClose();
  };

  const isSelected = (date: Dayjs) => !!value && date.isSame(value, 'day');

  const label = value ? value.format('DD-MM-YYYY') : dayjs().format('MMM YYYY');

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      sx={{
        px: 1,
        '&:hover': {
          backgroundColor: colors.dateFilter.triggerHoverBackground,
        },
      }}
    >
      <Typography
        fontSize="14px"
        sx={{
          color: open ? colors.dateFilter.triggerTextActive : colors.dateFilter.triggerText,
        }}
      >
        {label}
      </Typography>

      <IconButton
        onClick={handleClick}
        sx={{
          p: 0.5,
        }}
      >
        {open ? <CalendarIconActive /> : <CalendarIcon />}
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: `${colors.dateFilter.popoverBackground} !important`,
              backgroundImage: 'none !important',
              color: `${colors.dateFilter.popoverText} !important`,
              borderRadius: '8px',
              boxShadow: colors.dateFilter.popoverShadow,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            width: 260,
            background: `${colors.dateFilter.calendarBackground} !important`,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <IconButton
              size="small"
              onClick={() => canGoPrevMonth && setMonth(month.subtract(1, 'month'))}
              disabled={!canGoPrevMonth}
            >
              <RightArrowBoxIcon />
            </IconButton>

            <Typography fontWeight={600} sx={{ color: colors.dateFilter.calendarHeaderText }}>
              {month.format('MMM, YYYY')}
            </Typography>

            <IconButton
              size="small"
              onClick={() => canGoNextMonth && setMonth(month.add(1, 'month'))}
              disabled={!canGoNextMonth}
            >
              <LeftArrowBoxIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', mb: 1 }}>
            {weekDays.map((d) => (
              <Typography
                key={d}
                fontSize={12}
                textAlign="center"
                sx={{ color: colors.dateFilter.weekDayText }}
              >
                {d}
              </Typography>
            ))}
          </Box>

          {matrix.map((week, i) => (
            <Box key={i} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
              {week.map((date) => {
                const selected = isSelected(date);
                const disabledDate = isDisabledDate(date);
                const outsideMonth = !date.isSame(month, 'month');

                return (
                  <Box
                    key={date.toString()}
                    onClick={() => handleDateClick(date)}
                    sx={{
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: disabledDate ? 'not-allowed' : 'pointer',
                      opacity: disabledDate ? 0.35 : outsideMonth ? 0.65 : 1,
                      '&:hover': {
                        backgroundColor: disabledDate
                          ? 'transparent'
                          : `${colors.dateFilter.dayHoverBackground} !important`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: selected
                          ? colors.dateFilter.rangeBackground
                          : 'transparent',
                        color: selected
                          ? '#fff'
                          : disabledDate
                          ? colors.dateFilter.dayText
                          : colors.dateFilter.dayText,
                        '&:hover': {
                          color: disabledDate
                            ? colors.dateFilter.dayText
                            : `${selected ? '#fff' : colors.dateFilter.dayHoverText} !important`,
                        },
                      }}
                    >
                      {date.date()}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}
